/**
 * Field keys that require marketing data access — the `marketing_data_view`
 * permission plus the Lead Management module (see `hasMarketingDataAccess`).
 * Literals (not LEAD_PROPERTIES / APPOINTMENT_PROPERTIES / CUSTOMER_PROPERTIES)
 * so this util stays free of the component import graph. Keep in sync with
 * those constants.
 */
export const MARKETING_DATA_VIEW_FIELDS = [
  'leadSource',
  'appointment:lead_source',
  'marketingChannel',
  'marketingSource',
  'marketingCampaign',
  'customerIntegrations',
  // The customer table names the same two data points as the lead-side
  // `marketingChannel` / `marketingCampaign`, but by their raw UTM keys.
  'utm_medium',
  'utm_campaign',
] as const;

export type MarketingDataViewField = (typeof MARKETING_DATA_VIEW_FIELDS)[number];

export const isMarketingDataViewField = (field: unknown): field is MarketingDataViewField =>
  typeof field === 'string' &&
  (MARKETING_DATA_VIEW_FIELDS as readonly string[]).includes(field);

/** Drop marketing-gated fields unless the user has marketing data access. */
export function excludeMarketingDataViewFields<T>(
  fields: T[] | undefined,
  hasMarketingDataView: boolean,
): T[] {
  const list = fields || [];
  if (hasMarketingDataView) return list;
  return list.filter((field) => !isMarketingDataViewField(field));
}
